import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { extend } from '@thd-nucleus/data-sources';
import {
  ProductPod,
  PodSticker,
  ProductATC,
  ProductRating,
  ProductSample,
  ProductServiceAddOns,
  ProductIdentifier,
  ProductBadge,
  ProductSponsored,
  ProductImage,
  ProductFuelType,
  PodSection,
  PodTapper,
  ProductCustomOptions,
  PodSpacer,
  BundleDataModel,
  PodHeader,
  PodRow,
  ProductCompare
} from '@thd-olt-component-react/product-pod';
import { ProductSeeSimilarItems } from '@thd-olt-component-react/product-pod-group';
import { AddToList } from '@thd-olt-component-react/add-to-list';
import { KeyProductFeatures } from '@thd-olt-component-react/key-product-features';
import { FulfillmentPodShipping, FulfillmentPodStore } from '@thd-olt-component-react/fulfillment';
import { SuperSkuMiniSwatches } from '@thd-olt-component-react/super-sku';
import { Price, PriceClearanceDataModel } from '@thd-olt-component-react/price';
import { Promotion } from '@thd-olt-component-react/promotion';
import { ProductHeader } from '@thd-olt-component-react/product-details';
import { NonBuyableQuoteCenter } from '@thd-olt-component-react/non-buyable-quote-center';
import { useTheme } from '@thd-olt-component-react/theme-provider';
import { sendPodTapperAnalytics } from '@thd-olt-component-react/product-pod/dist/components/productPodUtils';

export const CustomProductPodTwoByTwoColumn = ({
  itemId,
  storeId,
  position,
  onClick,
  url,
  onCompare,
  onSelectedForCompare,
  selectedForCompare,
  showProjectQuantity,
  hasInStoreFilter,
  impProductPodData,
  isB2BCustomer,
  hasSameDayFilter,
  isBundleProduct,
  isFulfillmentFilterAndMsgEnabled
}) => {
  const theme = useTheme(CustomProductPodTwoByTwoColumn);
  const [superSkuChangedItemIds, setSuperSkuChangedItemIds] = useState([]);
  const { hideAddToListButton, hideAddToListIcon } = theme.props;
  const showFulfillmentPodStore = isFulfillmentFilterAndMsgEnabled ? !hasSameDayFilter : true;
  const showFulfillmentPodShipping = isFulfillmentFilterAndMsgEnabled ? !hasInStoreFilter : true;

  return (
    <ProductPod
      itemId={itemId}
      hasSSKU
      storeId={storeId}
      analyticsData={{ parent: 'plp', position }}
      analyticImpressionData={impProductPodData}
      gap="sui-p-2 sm:sui-pt-2 sm:sui-px-4 sm:sui-pb-4"
      render={(pod) => (
        <>
          <a
            className="sui-top-0 sui-left-0 sui-absolute sui-size-full sui-z-10"
            href={url}
            aria-label="Link"
          >
            <span className="sui-sr-only">View product details</span>
          </a>
          <div className="sui-flex sui-flex-col sui-gap-2 sui-h-full">
            <div className="sui-grid">
              <PodSpacer noPadding minHeight="32px">
                <PodHeader noPadding id="Slot-1-badges-and-heart">
                  <PodRow noPadding>
                    <ProductBadge itemId={pod.itemId} storeId={storeId} />
                    <ProductSponsored itemId={pod.itemId} position={position} />
                  </PodRow>
                  {!hideAddToListIcon && (
                    <PodTapper fitWidth zIndex>
                      <PodRow rowReverse noPadding>
                        <AddToList itemId={pod.itemId} showIconButton />
                      </PodRow>
                    </PodTapper>
                  )}
                </PodHeader>
              </PodSpacer>
            </div>
            <div
              className={`sui-grid lg:sui-grid-cols-3 xl:sui-grid-cols-2 lg:sui-grid-rows-1
              xl:sui-rows-2 sui-gap-4 sui-flex-1`}
            >
              <div className="sui-row-span-2">
                <PodSection
                  noPadding
                  columnAlign
                  gap="sui-gap-y-2"
                >
                  <PodTapper zIndex>
                    <PodSpacer padding={['T2']}>
                      <ProductImage
                        itemId={pod.itemId}
                        showSecondaryImage={pod.showSecondaryImage}
                      />
                    </PodSpacer>
                  </PodTapper>
                  <PodTapper
                    zIndex
                    aPointer
                    buttonPointer
                    cursorPointer
                    handleClick={(event) => sendPodTapperAnalytics(event, pod.itemId, url)}
                  >
                    {!isB2BCustomer && <ProductCustomOptions itemId={pod.itemId} />}
                    <ProductSample itemId={pod.itemId} />
                    <SuperSkuMiniSwatches
                      itemId={pod.itemId}
                      storeId={storeId}
                      onChange={(clickData) => {
                        if (!superSkuChangedItemIds.includes(clickData.itemId)) {
                          setSuperSkuChangedItemIds([...superSkuChangedItemIds, clickData.itemId]);
                        }
                        pod.onChange(clickData);
                      }}
                      onHover={pod.onSSKUHover}
                      componentImpressionData={
                        {
                          name: 'ProductPod',
                          component: 'ProductPod',
                          position: position + 1,
                          type: 'product'
                        }
                      }
                      hideComponentWhenEmpty
                      isImpressionsRequired
                    />
                    {!isB2BCustomer && <ProductServiceAddOns itemId={pod.itemId} />}
                  </PodTapper>
                </PodSection>
              </div>
              <div className="sui-grid sui-gap-2 sui-h-fit">
                <PodTapper fitWidth zIndex>
                  <PodSpacer minHeight="84px">
                    <ProductHeader
                      brand="inline"
                      brandFontSize="sm"
                      itemId={pod.itemId}
                      brandTitleMaxLine={4}
                      onClick={onClick}
                    />
                  </PodSpacer>
                </PodTapper>
                <PodTapper fitWidth zIndex>
                  <ProductRating itemId={pod.itemId} />
                </PodTapper>
                <ProductIdentifier itemId={pod.itemId} type="model" />
                <PodTapper
                  fitWidth
                  zIndex
                  handleClick={(event) => sendPodTapperAnalytics(event, pod.itemId, url)}
                >
                  <ProductFuelType itemId={pod.itemId} />
                </PodTapper>
                {isB2BCustomer && (
                  <>
                    <ProductIdentifier itemId={pod.itemId} type="itemId" />
                    <ProductIdentifier itemId={pod.itemId} type="storeSku" />
                  </>
                )}
                <div className="md:sui-hidden lg:sui-block">
                  <KeyProductFeatures
                    hideImage
                    storeId={storeId}
                    itemId={pod.itemId}
                    maxfeatures={3}
                    simple
                    oneColumnGrid
                  />
                </div>
              </div>
              <div
                className="xl:sui-col-start-2 xl:sui-row-start-2 sui-w-full sui-h-full lg:sui-row-span-1 sui-contents"
              >
                <PodSection noPadding columnAlign alignTop gap="sui-gap-4" marginTop="sui-mt-auto">
                  <PodTapper
                    zIndex
                    aPointer
                    buttonPointer
                    spanPointer
                    cursorPointer
                    handleClick={(event) => sendPodTapperAnalytics(event, pod.itemId, url)}
                  >
                    <PodSpacer minHeight="140px" noPadding>
                      <Price
                        itemId={pod.itemId}
                        large={false}
                        storeId={storeId}
                        displayEachUom={false}
                        onSavingsCenterToggle={pod.onSavingsCenterToggle}
                        showPreferredPricingBadge={isB2BCustomer}
                      />
                    </PodSpacer>
                  </PodTapper>
                  <PodTapper
                    zIndex
                    aPointer
                    buttonPointer
                    spanPointer
                    cursorPointer
                    handleClick={(event) => sendPodTapperAnalytics(event, pod.itemId, url)}
                  >
                    <PodSpacer noPadding>
                      <Promotion
                        itemId={pod.itemId}
                        hideConditionalPromoDescription
                        pageType="PLP"
                      />
                    </PodSpacer>
                  </PodTapper>
                  <PodTapper
                    zIndex
                    aPointer
                    buttonPointer
                    spanPointer
                    cursorPointer
                    handleClick={(event) => sendPodTapperAnalytics(event, pod.itemId, url)}
                  >
                    {
                      showFulfillmentPodStore
                        && (
                          <PodSpacer padding={['TOP', 'BOTTOM']} hideForProductTypes={['HDQC']}>
                            <FulfillmentPodStore itemId={pod.itemId} storeId={storeId} />
                          </PodSpacer>
                        )
                    }
                    {
                      showFulfillmentPodShipping
                        && (
                          <PodSpacer noPadding>
                            <FulfillmentPodShipping
                              itemId={pod.itemId}
                              storeId={storeId}
                              hasSameDayFilter={hasSameDayFilter}
                              showBundleFulfillmentMessage
                              showFasterPodSpacing
                              showFSDBMMessage
                            />
                          </PodSpacer>
                        )
                    }
                  </PodTapper>
                  <PodTapper zIndex>
                    <PodSpacer noPadding>
                      <ProductATC
                        variant={!hideAddToListButton ? 'primary' : 'secondary'}
                        hasInStoreFilter={hasInStoreFilter}
                        showProjectQuantity={showProjectQuantity}
                        storeId={storeId}
                        itemId={pod.itemId}
                        checkGeneric
                        outline
                      />
                    </PodSpacer>
                    {!isB2BCustomer && (
                      <ProductSeeSimilarItems
                        itemId={pod.itemId}
                        position={position}
                        productPodRef={pod.ref}
                        storeId={storeId}
                      />
                    )}
                    <PodSpacer disable={pod.productSubType !== 'HDQC'}>
                      <NonBuyableQuoteCenter
                        itemId={pod.itemId}
                        checkForProductSubType={['HDQC']}
                      />
                    </PodSpacer>
                    {!hideAddToListButton && (
                      <PodSpacer padding={['TOP']}>
                        <AddToList
                          itemId={pod.itemId}
                          storeId={storeId}
                        />
                      </PodSpacer>
                    )}
                  </PodTapper>
                </PodSection>
              </div>
            </div>
            <ProductCompare
              itemId={pod.itemId}
              onCompare={onCompare}
              onSelectedForCompare={onSelectedForCompare}
              selectedForCompare={selectedForCompare}
              tap
              noMargin
              flexPosition="lg:sui-justify-start"
              disableCompare={isBundleProduct}
            />
          </div>
        </>
      )}
    />
  );
};

CustomProductPodTwoByTwoColumn.displayName = 'CustomProductPodTwoByTwoColumn';

CustomProductPodTwoByTwoColumn.themeProps = {
  hideAddToListIcon: PropTypes.bool,
  hideAddToListButton: PropTypes.bool
};

CustomProductPodTwoByTwoColumn.defaultThemeProps = {
  hideAddToListIcon: false,
  hideAddToListButton: true
};

CustomProductPodTwoByTwoColumn.propTypes = {
  itemId: PropTypes.string.isRequired,
  storeId: PropTypes.string.isRequired,
  position: PropTypes.number.isRequired,
  onClick: PropTypes.func.isRequired,
  onSelectedForCompare: PropTypes.func.isRequired,
  onCompare: PropTypes.func.isRequired,
  selectedForCompare: PropTypes.arrayOf(PropTypes.string).isRequired,
  hasInStoreFilter: PropTypes.bool.isRequired,
  impProductPodData: PropTypes.shape({}),
  isB2BCustomer: PropTypes.bool.isRequired,
  hasSameDayFilter: PropTypes.bool,
  showProjectQuantity: PropTypes.bool,
  url: PropTypes.string.isRequired,
  isBundleProduct: PropTypes.bool,
  isFulfillmentFilterAndMsgEnabled: PropTypes.bool,
};

CustomProductPodTwoByTwoColumn.defaultProps = {
  impProductPodData: null,
  hasSameDayFilter: false,
  showProjectQuantity: false,
  isBundleProduct: false,
  isFulfillmentFilterAndMsgEnabled: false
};

CustomProductPodTwoByTwoColumn.dataModel = extend(
  ProductPod,
  ProductImage,
  ProductServiceAddOns,
  AddToList,
  Price,
  PriceClearanceDataModel,
  SuperSkuMiniSwatches,
  ProductATC,
  ProductRating,
  ProductSample,
  ProductHeader,
  ProductCompare,
  ProductIdentifier,
  ProductBadge,
  ProductFuelType,
  ProductSponsored,
  FulfillmentPodStore,
  FulfillmentPodShipping,
  ProductCustomOptions,
  KeyProductFeatures,
  ProductSeeSimilarItems,
  NonBuyableQuoteCenter,
  BundleDataModel,
  Promotion
);
